/**
 * @file useGameState.ts
 * @description
 * Custom React hook for managing the real-time state of a specific game room.
 * Subscribes to game-specific socket events (`stateUpdate`, `gameEnded`, etc.)
 * and exposes the current game state, loading status, and action functions (playCard, endTurn, hostAction).
 * Also manages and exposes chat messages for the game room.
 * Includes actions for players to concede the game or leave the game.
 *
 * @dependencies
 * - react: For state management and effects.
 * - react-router-dom: To get the game code from the URL.
 * - ../contexts/SocketContext: To get the socket instance.
 * - ../contexts/AuthContext: To get the current player's ID (socket ID) and name.
 * - ../models/piles (backend): To reuse validation logic (or redefine it frontend).
 *
 * **UPDATE:** Added `undoMove` action to trigger backend undo logic.
 */

import { useState, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom'; // Import useParams
import { useSocket } from '../contexts/SocketContext';
import { useAuth } from '../contexts/AuthContext';
// Import necessary types from backend models/services or define them here
// For simplicity, let's define the core types needed for the frontend GameState view
// Ideally, these types would be in a shared/types directory.

export type PileType = 'asc' | 'desc';

export interface Pile {
  id: string; // e.g., 'asc1', 'desc2'
  type: PileType;
  top: number;
}

interface Player {
  id: string; // Socket ID or unique player ID
  name: string;
}

type GameStatus = 'waiting' | 'started' | 'ended';

// Define the structure for the last move for undo functionality
interface Move {
  playerId: string;
  pileId: string;
  cardValue: number;
  pileValue: number;
}


// Frontend view of GameState - might omit some backend details like full deck or other players' raw hands
// This should mirror the backend GameState interface as much as possible for consistency
export interface GameState {
  gameId: string;
  code: string;
  status: GameStatus;
  hostId: string; // Socket ID of the host
  players: Player[];
  spectators: string[]; // Socket IDs of spectators
  piles: Record<string, Pile>;
  deck: number[]; // Frontend only needs deck size, but backend sends full deck. Let's keep full deck for now.
  hands: Record<string, number[]>; // Only the current player's hand is relevant to them
  currentPlayerIndex: number; // Index in the players array
  requiredPlays: number; // Minimum required plays this turn (backend tracks this)
  lastMove?: Move; // The last move played this turn (backend tracks this for undo)
  // Add other fields from backend GameItem as needed for display:
  lastActivity?: number; // Not strictly needed for UI, but keeping consistent with backend type
  createdAt?: number; // Not strictly needed for UI, but keeping consistent with backend type
  // Add temporary fields for game end info
  gameEndedResult?: 'win' | 'loss';
  gameEndedMessage?: string;
}

// Define the structure for a chat message received from the backend
interface ChatMessage {
    senderId: string;
    senderName: string;
    text: string;
    timestamp: number;
}


/**
 * Hook return type.
 */
interface UseGameStateReturn {
  state: GameState | null; // Current game state
  isLoading: boolean; // True while fetching initial state or waiting for gameStarted
  error: Error | null; // Any error (e.g., game not found, failed join, kicked)
  isSpectator: boolean; // True if the current user joined as a spectator
  chatMessages: ChatMessage[]; // Array of chat messages for this game room
  chatError: string | null; // Error specifically for chat (e.g., rate limit)
  // --- Actions (emit socket events) ---
  playCard: (pileId: string, card: number) => void;
  endTurn: () => void;
  undoMove: () => void; // Action to undo the last move played by the current player this turn
  hostAction: (type: string, payload: any) => void; // Generic host action emitter
  sendChatMessage: (text: string) => void; // Action to send a chat message
  concedeGame: () => void; // Action for current player to concede
  leaveGame: () => Promise<void>; // Action for any player/spectator to leave
  // --- Helper for UI ---
  validateMove: (pileId: string, cardValue: number) => boolean;
}

/**
 * useGameState
 * Manages the real-time state and interactions for a specific game room.
 * This hook reads the game code from the URL using useParams.
 */
const useGameState = (): UseGameStateReturn => { // Hook no longer takes code as prop
  const { code } = useParams<{ code: string }>(); // Get the code from the URL
  const { socket, isConnected, error: socketError } = useSocket(); // Get socket and its connection status/error
  const { playerName, isAuthenticated } = useAuth(); // Get player name and auth status
  const [state, setState] = useState<GameState | null>(null);
  const [isLoading, setIsLoading] = useState(true); // Start loading as we need to join
  const [error, setError] = useState<Error | null>(null); // Local error state for game logic/join
  const [isSpectator, setIsSpectator] = useState(false); // Track if the current user is a spectator
  const [chatMessages, setChatMessages] = useState<ChatMessage[]>([]); // State for chat messages
  const [chatError, setChatError] = useState<string | null>(null); // State for chat-related errors


  // Combine socket error with local error
  const combinedError = error || socketError;

  // Determine if the current client user is a player in the current state
  const isPlayer = state?.players?.some(p => p.id === socket?.id) || false;


  // --- Game Logic Helpers (Frontend Validation) ---
  // This is a duplication of backend logic but provides immediate UI feedback
  const validateMove = useCallback((pileId: string, cardValue: number): boolean => {
      if (!state) return false; // Cannot validate without state
      const pile = state.piles?.[pileId]; // Use optional chaining for safety
      if (!pile) {
          console.warn(`Frontend validation failed: Pile ${pileId} not found in state.`);
          return false; // Pile doesn't exist in current state
      }
      if (pile.type === 'asc') {
        // Ascending: card > top or exact reverse (top - 10)
        return cardValue > pile.top || cardValue === pile.top - 10;
      } else {
        // Descending: card < top or exact reverse (top + 10)
        return cardValue < pile.top || cardValue === pile.top + 10;
      }
  }, [state]); // Depend on state because pile values change


  // --- Socket Event Subscription & State Management ---
  useEffect(() => {
    // Wait for socket to be connected, authenticated, and have player name/code
    if (!socket || !isConnected || isAuthenticated === null || playerName === null || !code) {
         console.log('Skipping game state effect, waiting for dependencies:', { socket: !!socket, isConnected, isAuthenticated, playerName: !!playerName, code: !!code });
        // If we lose connection/auth state, error should be handled by Auth/Socket contexts.
        // If playerName/code is missing, that's a different issue.
        if (isAuthenticated === false || playerName === null) {
            // This case should ideally not happen if AuthRedirect works correctly,
            // but handle defensively.
            setError(new Error('Authentication or player name missing. Cannot join game.'));
            setIsLoading(false);
        }
         // Keep loading true while isAuthenticated is null
         if(isAuthenticated === null) setIsLoading(true);
        return;
    }

     // --- Socket Event Listeners ---
     // These handlers update the local `state` managed by this hook.
     // Note: These listeners are attached directly to the socket, and Socket.io
     // handles whether an event is targeted to this socket (e.g., via rooms).
     // The `if (update.state?.code === code)` check adds an extra layer of safety.

     const onStateUpdate = (update: { state: GameState }) => {
         // console.log(`Socket event: stateUpdate for ${code}`, update); // Too noisy during gameplay
         // Only update if the received state is for the game we are currently in
         if (update.state?.code === code) { // Add null check for update.state
              console.log(`Socket event: stateUpdate received for game ${code}. Status: ${update.state.status}. RequiredPlays: ${update.state.requiredPlays}. HasLastMove: ${!!update.state.lastMove}`);
              setState(update.state);
             // If the player was kicked, their ID might be missing from the player list.
             // The GameRoom component should handle detecting this and redirecting.
         }
     };

      const onGameEnded = (event: { result: 'win' | 'loss'; message?: string }) => {
          console.log(`Socket event: gameEnded for ${code}`, event);
          // Apply event if it matches current game code or if state was null but game code matches URL param
          if ((state?.code === code || !state) && code) { // Check against URL code if state is null
              console.log(`Game ${code} received 'gameEnded' event. Result: ${event.result}.`);
              // Update state status to ended and store result/message
              setState(prevState => {
                  // If prevState is null, create a minimal state with ended status
                  // This shouldn't happen often if joinGame provides initial state, but defensive.
                  const baseState = prevState || {
                      gameId: 'unknown', // Placeholder if state was null
                      code: code, // Use code from URL param
                      status: 'ended',
                      hostId: '', players: [], spectators: [],
                      piles: {}, deck: [], hands: {},
                      currentPlayerIndex: 0, requiredPlays: 0, lastMove: undefined, // Include lastMove in default state
                  };
                  return {
                      ...baseState,
                      status: 'ended',
                      gameEndedResult: event.result,
                      gameEndedMessage: event.message
                  };
              });
              // The GameRoom component should watch for state.status === 'ended' and display results/redirect.
          } else {
              console.warn(`Received 'gameEnded' event for non-matching or missing code. Expected: ${code}, Received (if state existed): ${state?.code}`);
          }
      };

      // Listen for explicit kick event targeting THIS socket
      const onKickedFromGame = (event: { code: string }) => {
          console.warn(`Socket event: kickedFromGame from ${event.code}.`);
          if (event.code === code) { // Ensure the kick is for the current game
               // This socket was explicitly kicked from this game.
               // Signal this as a specific error type.
                setError(new Error('You have been kicked from the game.')); // Simple error signal
                setIsLoading(false); // Stop loading indicator
                setState(null); // Clear game state
                setIsSpectator(false); // Ensure spectator status is false after kick
          }
      };

     const onHostReassigned = (event: { newHostId: string }) => {
         console.log(`Socket event: hostReassigned in game ${code} to ${event.newHostId}.`);
         // The stateUpdate should eventually reflect the new hostId, but this event
         // provides immediate feedback. Update the hostId in local state optimistically.
         if (state?.code === code) {
              setState(prevState => {
                  if (prevState) {
                      return { ...prevState, hostId: event.newHostId };
                  }
                  return null;
              });
         }
     };

       // We should listen to 'lobbyUpdate' and 'spectatorUpdate' events emitted to the game room
       // by the backend join/disconnect handlers. These are relevant for the waiting room view.
       const onLobbyUpdate = (event: { players: Player[]; hostId: string }) => {
            console.log(`Socket event: lobbyUpdate in game ${code}`, event);
            if (state?.code === code) {
                setState(prevState => {
                    if (prevState && prevState.status === 'waiting') { // Only apply if game is waiting
                         return { ...prevState, players: event.players, hostId: event.hostId };
                    }
                    // Also update players in started games if the list changes (e.g. kick/leave)
                     if (prevState && prevState.status === 'started') {
                         // For a started game, lobbyUpdate might only send player list, not host.
                         // We should merge carefully or assume backend sends full info.
                         // Let's assume backend sends players and host.
                         return { ...prevState, players: event.players, hostId: event.hostId };
                     }
                    return prevState; // Don't update if state is null or unexpected
                });
            }
       };

       const onSpectatorUpdate = (event: { spectators: string[] }) => {
            console.log(`Socket event: spectatorUpdate in game ${code}`, event);
            if (state?.code === code) {
                setState(prevState => {
                    if (prevState) {
                         return { ...prevState, spectators: event.spectators };
                    }
                    return null;
                });
            }
       };

       const onInvalidMove = (event: { pileId: string; card: number }) => {
            console.warn(`Socket event: invalidMove received for game ${code}: card ${event.card} on pile ${event.pileId}.`);
            // Optionally show UI feedback to the player.
            // alert(`Invalid Move: Cannot play card ${event.card} on the selected pile.`); // Simple alert for now
            // Set a chat error message? Or a separate game error?
             setChatError('Invalid move!'); // Simple error for chat area
             setTimeout(() => setChatError(null), 3000); // Clear error after 3 seconds
       };

        // --- Chat Event Listener ---
        const onChatMessage = (message: ChatMessage) => {
             console.log(`Socket event: chatMessage received for game ${code}.`, message);
             // Only add message if it's for the current game room
             if (state?.code === code || !state) { // Assuming messages are global within the room
                 setChatMessages(prevMessages => [...prevMessages, message]);
             }
        };


     // --- Attach Listeners ---
     console.log(`Attaching socket listeners for game room: ${code}`);
     socket.on('stateUpdate', onStateUpdate);
     socket.on('gameEnded', onGameEnded);
     socket.on('kickedFromGame', onKickedFromGame); // Listen for *this* player being kicked
     socket.on('hostReassigned', onHostReassigned);
     socket.on('lobbyUpdate', onLobbyUpdate); // Listen for player/host changes in waiting room view
     socket.on('spectatorUpdate', onSpectatorUpdate); // Listen for spectator changes
     socket.on('invalidMove', onInvalidMove); // Listen for backend validation failures
     socket.on('chatMessage', onChatMessage); // Listen for chat messages


    // --- Attempt to Join the Game Room ---
    // Only attempt to join if we don't have state loaded for THIS code,
    // or if the state we have is for a game that was ended (maybe try rejoining?).
    // The `joinGame` socket event handles fetching initial state and adding the socket to the room.
    // If state exists and matches the code and isn't ended, we assume we are already in the room
    // and the existing state is sufficient, relying on listeners for updates.
     if (!state || state.code !== code || state.status === 'ended') {
         console.log(`Attempting to join game room: ${code}. Current state: ${state ? state.status : 'null'}.`);
         setIsLoading(true); // Set loading true while waiting for join ack
         setError(null); // Clear previous errors
         setChatMessages([]); // Clear chat messages on new join attempt

         socket.emit('joinGame', { code, name: playerName }, (resp: { success?: boolean; error?: string; state?: GameState; isSpectator?: boolean }) => {
             if (resp.error) {
                 console.error(`Failed to join game ${code} via socket:`, resp.error);
                 setError(new Error(resp.error));
                 setIsLoading(false);
             } else if (resp.success && resp.state) {
                 console.log(`Successfully joined game ${code}. Received initial state. Is Spectator: ${resp.isSpectator}`);
                 setState(resp.state);
                 setIsSpectator(resp.isSpectator || false); // Default to false if undefined
                 setIsLoading(false);

                 // If the game is already ended, the state will reflect this,
                 // and GameRoom will render the end screen. No special handling needed here.

             } else {
                  // Should not happen based on backend contract, but handle defensively
                  console.error(`Failed to join game ${code}: Unknown response`, resp);
                  setError(new Error('Failed to join game: Unknown server response.'));
                  setIsLoading(false);
             }
         });
     } else {
          console.log(`State for game ${code} already loaded (status: ${state.status}). Skipping joinGame emission.`);
          setIsLoading(false); // Ensure loading is false if state is already present
          setError(null); // Clear errors if state is now valid
     }


    // Cleanup function to remove listeners when the component unmounts or dependencies change
    return () => {
      console.log(`Cleaning up socket listeners for game room: ${code}`);
      // Use specific unsubscribe
      if(socket) { // Check socket exists before off
          socket.off('stateUpdate', onStateUpdate);
          socket.off('gameEnded', onGameEnded);
          socket.off('kickedFromGame', onKickedFromGame);
          socket.off('hostReassigned', onHostReassigned);
          socket.off('lobbyUpdate', onLobbyUpdate);
          socket.off('spectatorUpdate', onSpectatorUpdate);
          socket.off('invalidMove', onInvalidMove);
          socket.off('chatMessage', onChatMessage); // Clean up chat listener
      }
       // An explicit 'leaveGame' event is now used for intentional leaves.
       // Disconnect handler on the backend handles unintentional disconnects.
    };

     // Dependencies include socket, connection state, auth state, player name, and code from useParams.
     // state is used inside the listener callbacks to check state?.code === code.
     // playerName is used in joinGame emit.
     // isAuthenticated is used to determine if we should even attempt to join.
  }, [socket, isConnected, isAuthenticated, playerName, code, state?.code]); // Depend on state?.code to re-run ONLY if the game code in state changes

  // --- Actions (emit socket events) ---

  const playCard = useCallback((pileId: string, card: number) => {
    if (!socket || !isConnected || !state) {
        console.warn('Cannot play card: socket not ready or state missing.');
        // TODO: Show error to user?
        return; // Cannot play if socket not connected or state not loaded
    }
    const playerId = socket.id;
    const currentTurnPlayer = state.players[state.currentPlayerIndex];
     if (!currentTurnPlayer || playerId !== currentTurnPlayer.id) {
         console.warn('Cannot play card: Not your turn.');
          // TODO: Show error to user?
         return;
     }
     const playerHand = state.hands[playerId];
     if (!playerHand || !playerHand.includes(card)) {
          console.warn('Cannot play card: Card not in hand.');
           // TODO: Show error to user?
          return;
     }

    // Basic client-side validation before sending
     if (!validateMove(pileId, card)) {
         console.warn(`Attempted to play invalid move client-side: card ${card} on pile ${pileId}`);
          // The backend will also validate and send 'invalidMove' event.
          // UI could react immediately here too.
         return; // Do not emit socket event if invalid client-side
     }

     console.log(`Emitting playCard event: { code: ${code}, pileId: ${pileId}, card: ${card} }`);
    socket.emit('playCard', { code, pileId, card }, (resp: { success?: boolean; error?: string; requiredPlaysRemaining?: number }) => {
         if (resp.error) {
              console.error('playCard failed on backend:', resp.error);
              // TODO: Handle error: maybe show message, revert optimistic update if implemented
              // The backend broadcasts `stateUpdate` on success, which will correct the state.
              // The backend also broadcasts 'invalidMove' event on server-side validation failure.
         } else {
             console.log('playCard success acknowledgement. Required plays remaining:', resp.requiredPlaysRemaining);
             // Optionally, update requiredPlays optimistically here based on ack response
             // This would make the 'End Turn' button enable/disable faster
             // setState(prevState => prevState ? { ...prevState, requiredPlays: resp.requiredPlaysRemaining ?? prevState.requiredPlays } : null);
         }
    });
     // TODO: Implement optimistic update here: remove card from hand, update pile top value locally
     // This would make the UI feel faster, but requires rollback logic on error.
     // For now, rely on the 'stateUpdate' broadcast from the backend.

  }, [socket, isConnected, state, code, validateMove]); // Depend on socket, state, code, and validateMove

  const undoMove = useCallback(() => {
      if (!socket || !isConnected || !state || state.status !== 'started') {
          console.warn('Cannot undo move: socket not ready, state missing, or game not started.');
           // TODO: Show error to user?
          return;
      }
      const playerId = socket.id;
      const currentTurnPlayer = state.players[state.currentPlayerIndex];
       if (!currentTurnPlayer || playerId !== currentTurnPlayer.id) {
           console.warn('Cannot undo move: Not your turn.');
            // TODO: Show error to user?
           return;
       }
       if (!state.lastMove) {
            console.warn('Cannot undo move: No last move to undo.');
             // TODO: Show error to user?
            return;
       }
       // Backend validates lastMove.playerId matches current player.
       // Client-side, we only need to check if state.lastMove exists to enable the button.

       console.log(`Emitting undoMove event: { code: ${code} }`);
       socket.emit('undoMove', { code }, (resp: { success?: boolean; error?: string }) => {
           if (resp.error) {
                console.error('undoMove failed on backend:', resp.error);
                // TODO: Handle error message
                alert(`Undo failed: ${resp.error}`); // Simple alert
           } else {
                console.log('undoMove success acknowledgement.');
                // Backend will broadcast stateUpdate with reverted pile, hand, incremented requiredPlays, and lastMove=undefined
           }
       });
       // TODO: Implement optimistic update for undo? Revert pile, add card back, increment requiredPlays, clear lastMove.
       // This would require careful state management and rollback on backend failure ack.
       // For now, rely on the 'stateUpdate' broadcast from the backend.
  }, [socket, isConnected, state, code]); // Depend on socket, state, code


  const endTurn = useCallback(() => {
    if (!socket || !isConnected || !state) {
        console.warn('Cannot end turn: socket not ready or state missing.');
        return; // Cannot end turn if socket not connected or state not loaded
    }
    const playerId = socket.id;
    const currentTurnPlayer = state.players[state.currentPlayerIndex];
     if (!currentTurnPlayer || playerId !== currentTurnPlayer.id) {
         console.warn('Cannot end turn: Not your turn.');
          // TODO: Show error to user?
         return;
     }

    // Basic client-side check if enough plays were made before emitting
    // Rule: 2 plays required while deck > 0, 1 play required after deck empty.
    // The backend's `requiredPlays` is the number *remaining*. If <= 0, requirement met.
     if (state.requiredPlays > 0) {
         console.warn(`Attempted to end turn client-side without meeting required plays. Required remaining: ${state.requiredPlays}`);
          // Optionally, show a warning message to the user
          alert(`You must play ${state.requiredPlays} more card${state.requiredPlays > 1 ? 's' : ''}.`); // Simple alert for now
         return; // Do not emit socket event
     }


    console.log(`Emitting endTurn event: { code: ${code} }`);
    socket.emit('endTurn', { code }, (resp: { success?: boolean; error?: string }) => {
        if (resp.error) {
             console.error('endTurn failed on backend:', resp.error);
             // TODO: Handle error message
        } else {
             console.log('endTurn success acknowledgement.');
             // Backend will broadcast stateUpdate with new turn/requiredPlays/hands
        }
    });
  }, [socket, isConnected, state, code]); // Depend on socket, state, code

    const hostAction = useCallback((type: string, payload: any) => {
        if (!socket || !isConnected || !state) {
            console.warn(`Cannot emit host action '${type}': socket not ready or state missing.`);
            return;
        }
        if (socket.id !== state.hostId) {
             console.warn(`Attempted to emit host action '${type}' but not host.`);
             // Optionally show an error message
             alert(`Host action failed: You are not the host.`); // Simple alert
             return;
        }

        console.log(`Emitting hostAction event: { code: ${code}, type: ${type}, payload: ${JSON.stringify(payload)} }`);
        socket.emit('hostAction', { code, type, payload }, (resp: { success?: boolean; error?: string; state?: GameState }) => {
            if (resp.error) {
                console.error(`hostAction '${type}' failed on backend:`, resp.error);
                 // TODO: Handle error message
                 alert(`Host action failed: ${resp.error}`); // Simple alert
            } else {
                 console.log(`hostAction '${type}' success acknowledgement.`);
                 // Backend will broadcast stateUpdate or specific events (hostReassigned, playerKicked, gameEnded)
                 // If state is returned in the ack (like for kick), could update optimistically, but relying on broadcast is safer.
                 // if (resp.state) {
                 //     setState(resp.state); // Update state directly from ack if backend sends it back
                 // }
            }
        });
    }, [socket, isConnected, state, code]); // Depend on socket, state, code

    // --- Chat Action ---
    const sendChatMessage = useCallback((text: string) => {
        if (!socket || !isConnected || !code || !isPlayer) { // Ensure socket, code, and player status
            console.warn('Cannot send chat message: socket not ready, code missing, or not a player.');
             setChatError('Cannot send message.'); // Indicate failure to the user
             setTimeout(() => setChatError(null), 3000);
            return;
        }
         console.log(`Emitting chatMessage event for game ${code}: "${text}"`);
        socket.emit('chatMessage', { code, text }); // Backend adds sender name and timestamp
        // Note: We don't add the message to local state here optimistically,
        // we wait for the backend broadcast ('onChatMessage') to keep state synced.
    }, [socket, isConnected, code, isPlayer]); // Depend on socket, connection, code, and whether the user is a player


    /**
     * concedeGame
     * Allows the current player to trigger a game loss.
     */
    const concedeGame = useCallback(() => {
         if (!socket || !isConnected || !state) {
             console.warn('Cannot concede: socket not ready or state missing.');
             // TODO: Show error to user?
             return;
         }
         const playerId = socket.id;
         const currentTurnPlayer = state.players[state.currentPlayerIndex];
         // Allow conceding even if not your turn, but backend might restrict.
         // Let's allow anytime you are a player for simplicity client-side.
         const isCurrentPlayer = currentTurnPlayer?.id === playerId;
         if (!isPlayer) {
             console.warn('Cannot concede: You are not a player.');
             alert('Only players can concede.');
             return;
         }
          // Restrict to current player's turn based on backend handler logic
         if (!isCurrentPlayer) {
              console.warn('Cannot concede: Not your turn.');
              alert('You can only concede on your turn.');
              return;
         }

         console.log(`Emitting concedeGame event: { code: ${code} }`);
         socket.emit('concedeGame', { code }, (resp: { success?: boolean; error?: string }) => {
             if (resp.error) {
                 console.error('concedeGame failed on backend:', resp.error);
                 alert(`Failed to concede: ${resp.error}`); // Show error to user
             } else {
                 console.log('concedeGame success acknowledgement.');
                 // Backend will broadcast gameEnded
             }
         });
    }, [socket, isConnected, state, code, isPlayer]); // Depend on socket, state, code, isPlayer


    /**
     * leaveGame
     * Allows any player or spectator to leave the game.
     * Navigating to the lobby happens after the socket event is emitted.
     */
     const leaveGame = useCallback((): Promise<void> => {
         return new Promise((resolve, reject) => {
             if (!socket || !isConnected || !code) {
                 console.warn('Cannot leave game: socket not ready or code missing.');
                  // Reject immediately and let the UI handle navigation
                 return reject(new Error('Socket not connected. Cannot leave game.'));
             }

             console.log(`Emitting leaveGame event: { code: ${code} }`);
             socket.emit('leaveGame', { code }, (resp: { success?: boolean; error?: string }) => {
                  if (resp.error) {
                     console.error('leaveGame failed on backend:', resp.error);
                     alert(`Failed to leave game: ${resp.error}`); // Show error to user
                     return reject(new Error(resp.error));
                 } else {
                      console.log('leaveGame success acknowledgement.');
                      // Backend state is updated, UI should now navigate away.
                      resolve(); // Resolve promise so caller can navigate
                 }
             });
              // Note: There is no timeout added here for simplicity.
              // If the ack is not received, the client might navigate away anyway.
         });
     }, [socket, isConnected, code]); // Depend on socket, connection, and code


  // Return the state and actions
  return {
    state,
    isLoading,
    error: combinedError, // Expose combined error
    isSpectator,
    chatMessages, // Expose chat messages
    chatError, // Expose chat error
    playCard,
    undoMove, // Expose undoMove action
    endTurn,
    hostAction,
    sendChatMessage, // Expose send chat message action
    concedeGame, // Expose concede action
    leaveGame, // Expose leave game action
    validateMove, // Expose frontend validation helper for UI components
  };
};

export default useGameState;
